import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isTokenExpired } from './auth';
import { checkCashierCounterStatus } from '../api/dayCloseApi';
import CounterClosedBlock from '../components/CounterClosedBlock';
import { usePagePathPermission } from './pagePermissions';

// Define allowed paths per userType (all lowercase)
const roleBasedAccess = {
  cashier: [
    "/dashboard/cashier",
    "/dashboard/cashier/analytics",
    "/collection/barcode-payment",
    "/collection/manual-payment",
    "/collection/paid-amounts",
    "/collection/paid-summary",
    "/collection/counter-close",
    "/sale/posgst",
    "/sale/newpos",
    "/sale/pos",
    "/sale/newsale",
    "/sale/vatsale",
    "/reports/billhistory",
    "/reports/saleledger",
    "/vouchers/recieptvoucher",
    "/vouchers/paymentvoucher",
    "/logout",
  ],
  promoter: [
    "/dashboard/cashier",
    "/dashboard/cashier/analytics",
    "/collection/barcode-payment",
    "/collection/manual-payment",
    "/collection/paid-amounts",
    "/collection/paid-summary",
    "/collection/counter-close",
    "/sale/posgst",
    "/sale/newpos",
    "/sale/pos",
    "/sale/newsale",
    "/sale/vatsale",
    "/reports/billhistory",
    "/reports/saleledger",
    "/vouchers/recieptvoucher",
    "/vouchers/paymentvoucher",
    "/logout",
  ],
  account: [
    "/dashboard/account",
    "/accounts/dashboard",
    "/collection/day-close",
    "/collection/summary",
    "/ledgers/general-entry",
    "/ledgers/suppliersledger",
    "/ledgers/customerledger",
    "/vouchers/recieptvoucher",
    "/vouchers/paymentvoucher",
    "/reports/billhistory",
    "/reports/saleledger",
    "/reports/supplierledger",
    "/reports/item-wise-sale-report",
    "/reports/bill-wise-sale",
    "/reports/date-wise-sale",
    "/reports/cashier-summaries",
    "/reports/table-history",
    "/users/editprofile",
    // Structurally reachable by account, but OFF by default - actual access
    // is gated per-user by page_registry (bill_management.cancel_bill /
    // move_entertainment have account in applicableRoles but not
    // defaultEnabledRoles), so an Admin must explicitly enable it via
    // Setting > Page Permissions before a given account user can use it.
    "/bill-management/cancel-bill",
    "/bill-management/move-entertainment",
    "/logout",
  ],
  cstoremanager: [
    "/dashboard/clubstoremanager",
    "/report/clubstoremanager",
    "/logout",
  ],
  
  wstoremanager: [
    "/dashboard/warehousestoremanager",
    "/dashboard/warehousestoremanager/report",
    "/inventory/stockin",
    "/inventory/stockout",
    "/inventory/stockinout",
    "/inventory/stockreports",
    "/inventory/closing-stock",
    "/inventory/stock-report-new",
    "/inventory/assets",
    "/logout",
  ],
  modifier: [
    "/bill-management/cancel-bill",
    "/bill-management/move-entertainment",
    "/collection/summary",
    "/logout",
  ],
  hr: [
    "/hr/analytics-dashboard",
    "/hr/employee-master",
    "/hr/employee-records",
    "/hr/departments",
    "/hr/positions",
    "/hr/work-schedule",
    "/hr/payroll-management",
    "/hr/payroll-reports",
    "/hr/employee-payroll-system",
    "/hr/tax-compliances",
    "/users/newuser",
    "/users/editprofile",
    "/users/login-history",
    "/logout",
  ],
  admin: "all", // admin can access all routes
  // Add other roles here if needed
};

const PrivateRoute = ({ children }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [counterBlocked, setCounterBlocked] = useState(false);
  const [blockData, setBlockData] = useState(null);
  
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const userType = (localStorage.getItem('usertype') || sessionStorage.getItem('usertype') || '').toLowerCase();
  const userName = localStorage.getItem('uname') || sessionStorage.getItem('uname');

  // Per-user page_registry override, on top of the static roleBasedAccess
  // gate below - lets an Admin disable one specific page for one specific
  // user without touching the role's own allowed-paths list. Admin itself
  // is exempt (see PERMISSION_GOVERNED_ROLES in pagePermissions.js).
  const { allowed: pagePermitted, loading: pagePermLoading } = usePagePathPermission(
    location.pathname.toLowerCase()
  );

  useEffect(() => {
    const checkCashierAccess = async () => {
      // Only check for cashiers and promoters
      if ((userType === 'cashier' || userType === 'promoter') && userName && token && !isTokenExpired(token)) {
        setLoading(true);
        try {
          console.log(`PrivateRoute: Checking counter status for ${userType}...`);
          
          const counterStatus = await checkCashierCounterStatus(userName);
          
          if (!counterStatus.canLogin && counterStatus.isClosed) {
            console.log('PrivateRoute: Counter is closed, blocking access...');
            setCounterBlocked(true);
            setBlockData({
              cashierName: userName,
              cashierSummary: counterStatus.cashierSummary,
              currentBillingDate: counterStatus.currentBillingDate
            });
          } else {
            setCounterBlocked(false);
            setBlockData(null);
          }
        } catch (error) {
          console.error('PrivateRoute: Error checking counter status:', error);
          // On error, allow access to prevent system lockout
          setCounterBlocked(false);
          setBlockData(null);
        } finally {
          setLoading(false);
        }
      }
    };

    checkCashierAccess();
  }, [userType, userName, token, location.pathname]);

  // Check basic authentication
  if (!token || isTokenExpired(token)) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!userType) {
    return <Navigate to="/accessdenied" state={{ from: location }} replace />;
  }

  // Check role-based access
  const allowedPaths = roleBasedAccess[userType];
  if (allowedPaths && allowedPaths !== "all" && !allowedPaths.includes(location.pathname.toLowerCase())) {
    return <Navigate to="/accessdenied" replace />;
  }
  
  // If user type is not defined in roleBasedAccess, deny access
  if (!allowedPaths) {
    return <Navigate to="/accessdenied" replace />;
  }

  // Show loading state while checking counter status or page permissions
  if (loading || pagePermLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Checking access...</span>
        </div>
      </div>
    );
  }

  // Show blocked page if counter is closed for cashiers
  if (counterBlocked) {
    return (
      <CounterClosedBlock
        cashierName={blockData?.cashierName}
        cashierSummary={blockData?.cashierSummary}
        currentBillingDate={blockData?.currentBillingDate}
      />
    );
  }

  // An Admin has explicitly disabled this page for this specific user via
  // Setting > Page Permissions - block regardless of what roleBasedAccess
  // says, same destination as the role-based denial above.
  if (!pagePermitted) {
    return <Navigate to="/accessdenied" replace />;
  }

  // Render the protected content
  return children;
};

export default PrivateRoute;
